import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Loader,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCircleFilled,

  IconQuestionMark,
  IconRadar,
  IconRocket,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  SAMSUNG_CATALOG,
  SAMSUNG_RETAILER_MATCHES,
} from "../../mock/discovery";
import { LogoImg } from "../../components/LogoImg";
import type {
  DiscoveredBrandCatalog,
  DiscoveredProductLine,
  MatchConfidence,
  ProductLineRetailerMatches,
} from "../../mock/discovery";
import { formatPriceCompact } from "../../utils/price";

// --- Helpers ---


function tierColor(tier: string): string {
  switch (tier) {
    case "flagship":
      return "violet";
    case "premium":
      return "blue";
    case "mid-range":
      return "teal";
    case "value":
      return "gray";
    default:
      return "gray";
  }
}

function confidenceIcon(c: MatchConfidence) {
  switch (c) {
    case "high":
      return (
        <ThemeIcon size="xs" color="green" variant="light" radius="xl">
          <IconCheck size={10} />
        </ThemeIcon>
      );
    case "medium":
      return (
        <ThemeIcon size="xs" color="yellow" variant="light" radius="xl">
          <IconQuestionMark size={10} />
        </ThemeIcon>
      );
    case "low":
      return (
        <ThemeIcon size="xs" color="orange" variant="light" radius="xl">
          <IconQuestionMark size={10} />
        </ThemeIcon>
      );
    case "not_found":
      return (
        <ThemeIcon size="xs" color="gray" variant="light" radius="xl">
          <IconX size={10} />
        </ThemeIcon>
      );
  }
}

function confidenceBadge(c: MatchConfidence) {
  const colors: Record<MatchConfidence, string> = {
    high: "green",
    medium: "yellow",
    low: "orange",
    not_found: "gray",
  };
  const labels: Record<MatchConfidence, string> = {
    high: "Match",
    medium: "Verify",
    low: "Uncertain",
    not_found: "Not found",
  };
  return (
    <Badge size="xs" color={colors[c]} variant="light">
      {labels[c]}
    </Badge>
  );
}

// --- Step 1: Brand Selection + Crawl ---

function StepCrawl({
  catalog,
  onCrawlComplete,
  onReview,
}: {
  catalog: DiscoveredBrandCatalog | null;
  onCrawlComplete: (catalog: DiscoveredBrandCatalog) => void;
  onReview: () => void;
}) {
  const [brand, setBrand] = useState<string | null>(null);
  const [crawling, setCrawling] = useState(false);
  const [progress, setProgress] = useState(0);

  function startCrawl() {
    if (!brand) return;
    setCrawling(true);
    setProgress(0);

    const steps = [10, 25, 45, 65, 80, 95, 100];
    steps.forEach((p, i) => {
      setTimeout(() => {
        setProgress(p);
        if (p === 100) {
          setTimeout(() => {
            setCrawling(false);
            onCrawlComplete(SAMSUNG_CATALOG);
          }, 300);
        }
      }, (i + 1) * 400);
    });
  }

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed" maw={500}>
        Select a brand to crawl their website for the complete TV product
        catalog. The system will extract all product lines, size variants, model
        numbers, and MSRPs.
      </Text>

      <Select
        label="Brand"
        placeholder="Select brand to crawl"
        data={[
          { value: "samsung", label: "Samsung — samsung.com/us/televisions" },
          { value: "lg", label: "LG — lg.com/us/tvs" },
          { value: "tcl", label: "TCL — tcl.com/us/tvs" },
          { value: "hisense", label: "Hisense — hisense-usa.com/tvs" },
        ]}
        value={brand}
        onChange={setBrand}
        disabled={crawling || !!catalog}
        maw={500}
      />

      {crawling && (
        <Card withBorder padding="md" maw={500}>
          <Group gap="sm" mb="xs">
            <Loader size="xs" />
            <Text size="sm" fw={500}>
              Crawling samsung.com...
            </Text>
          </Group>
          <Progress value={progress} animated size="sm" mb="xs" />
          <Text size="xs" c="dimmed">
            {progress < 30
              ? "Discovering product categories..."
              : progress < 60
                ? "Extracting product lines and variants..."
                : progress < 90
                  ? "Reading prices and specifications..."
                  : "Finalizing catalog..."}
          </Text>
        </Card>
      )}

      {!crawling && !catalog && (
        <Box>
          <Button
            leftSection={<IconRadar size={16} />}
            disabled={!brand}
            onClick={startCrawl}
          >
            Crawl Product Catalog
          </Button>
        </Box>
      )}

      {!crawling && catalog && (
        <CrawlResultsSummary catalog={catalog} onReview={onReview} />
      )}
    </Stack>
  );
}

function CrawlResultsSummary({
  catalog,
  onReview,
}: {
  catalog: DiscoveredBrandCatalog;
  onReview: () => void;
}) {
  const totalVariants = catalog.productLines.reduce(
    (s, pl) => s + pl.variants.length,
    0,
  );
  const tiers = {
    flagship: catalog.productLines.filter((pl) => pl.tier === "flagship"),
    premium: catalog.productLines.filter((pl) => pl.tier === "premium"),
    "mid-range": catalog.productLines.filter((pl) => pl.tier === "mid-range"),
    value: catalog.productLines.filter((pl) => pl.tier === "value"),
  };

  return (
    <Card withBorder padding="md" style={{ borderColor: "var(--mantine-color-green-4)" }}>
      <Group gap="sm" mb="md">
        <ThemeIcon size="sm" color="green" variant="light" radius="xl">
          <IconCheck size={12} />
        </ThemeIcon>
        <Text fw={600} size="sm">
          Crawl complete
        </Text>
        <Text size="xs" c="dimmed">
          — {catalog.productLines.length} product lines, {totalVariants} SKUs found
        </Text>
      </Group>

      <SimpleGrid cols={4} mb="md">
        {(Object.entries(tiers) as [string, DiscoveredProductLine[]][]).map(
          ([tier, lines]) =>
            lines.length > 0 && (
              <Box key={tier}>
                <Badge
                  size="xs"
                  color={tierColor(tier)}
                  variant="light"
                  mb={4}
                >
                  {tier}
                </Badge>
                {lines.map((pl) => (
                  <Text key={pl.id} size="xs" c="dimmed">
                    {pl.name} ({pl.variants.length} sizes)
                  </Text>
                ))}
              </Box>
            ),
        )}
      </SimpleGrid>

      <Button onClick={onReview} leftSection={<IconCheck size={16} />}>
        Review &amp; Select Product Lines
      </Button>
    </Card>
  );
}

// --- Step 2: Review Catalog ---

function StepReviewCatalog({
  catalog,
  selections,
  onToggle,
}: {
  catalog: DiscoveredBrandCatalog;
  selections: Record<string, boolean>;
  onToggle: (id: string) => void;
}) {
  const selectedCount = Object.values(selections).filter(Boolean).length;
  const selectedVariants = catalog.productLines
    .filter((pl) => selections[pl.id])
    .reduce((sum, pl) => sum + pl.variants.length, 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Box>
          <Text size="sm" c="dimmed">
            Crawled{" "}
            <Anchor href={catalog.crawledUrl} target="_blank" size="sm">
              {catalog.crawledUrl.replace("https://www.", "")}
            </Anchor>
          </Text>
          <Text size="sm" c="dimmed">
            Found {catalog.productLines.length} product lines ·{" "}
            {catalog.productLines.reduce(
              (s, pl) => s + pl.variants.length,
              0,
            )}{" "}
            total SKUs
          </Text>
        </Box>
        <Badge size="lg" variant="light">
          {selectedCount} selected · {selectedVariants} SKUs
        </Badge>
      </Group>

      <Text size="xs" c="dimmed">
        Select the product lines you want to track. Unselected lines will not be
        imported.
      </Text>

      {catalog.productLines.map((pl) => (
        <CatalogLineCard
          key={pl.id}
          line={pl}
          selected={selections[pl.id] ?? false}
          onToggle={() => onToggle(pl.id)}
        />
      ))}
    </Stack>
  );
}

function CatalogLineCard({
  line,
  selected,
  onToggle,
}: {
  line: DiscoveredProductLine;
  selected: boolean;
  onToggle: () => void;
}) {
  const msrps = line.variants.map((v) => v.msrp).sort((a, b) => a - b);
  const msrpRange =
    msrps.length === 1
      ? formatPriceCompact(msrps[0])
      : `${formatPriceCompact(msrps[0])} – ${formatPriceCompact(msrps[msrps.length - 1])}`;

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        opacity: selected ? 1 : 0.55,
        borderColor: selected ? "var(--mantine-primary-color-light-color)" : undefined,
        cursor: "pointer",
      }}
      onClick={onToggle}
    >
      <Group justify="space-between">
        <Group gap="sm">
          <Checkbox
            checked={selected}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
          />
          <Box>
            <Group gap="xs">
              <Text fw={600} size="sm">
                {line.name}
              </Text>
              <Badge size="xs" color={tierColor(line.tier)} variant="light">
                {line.tier}
              </Badge>
              <Badge size="xs" variant="light" color="gray">
                {line.displayTechnology}
              </Badge>
              <Badge size="xs" variant="light" color="gray">
                {line.resolution}
              </Badge>
            </Group>
            <Group gap="xs" mt={4}>
              {line.variants.map((v) => (
                <Badge key={v.variantLabel} variant="light" color="gray" size="xs">
                  {v.variantLabel}
                </Badge>
              ))}
              <Text size="xs" c="dimmed">
                · {msrpRange}
              </Text>
            </Group>
          </Box>
        </Group>
        <Text size="sm" c="dimmed">
          {line.variants.length} SKUs
        </Text>
      </Group>
    </Card>
  );
}

// --- Step 3: Retailer Matching ---

function StepRetailerMatching({
  matches,
  searching,
  searchProgress,
}: {
  matches: ProductLineRetailerMatches[];
  searching: boolean;
  searchProgress: number;
}) {
  if (searching) {
    return (
      <Stack gap="md" maw={600}>
        <Card withBorder padding="md">
          <Group gap="sm" mb="xs">
            <Loader size="xs" />
            <Text size="sm" fw={500}>
              Searching retailers for matching products...
            </Text>
          </Group>
          <Progress value={searchProgress} animated size="sm" mb="xs" />
          <Text size="xs" c="dimmed">
            Searching {searchProgress < 20 ? "Amazon" : searchProgress < 40 ? "Best Buy" : searchProgress < 60 ? "Walmart" : searchProgress < 80 ? "Target" : "Costco"}...
          </Text>
        </Card>
      </Stack>
    );
  }

  // Summary stats
  const allMatches = matches.flatMap((pl) =>
    pl.variants.flatMap((v) => v.matches),
  );
  const highCount = allMatches.filter((m) => m.confidence === "high").length;
  const mediumCount = allMatches.filter(
    (m) => m.confidence === "medium",
  ).length;
  const notFoundCount = allMatches.filter(
    (m) => m.confidence === "not_found",
  ).length;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={4}>
        <Card withBorder padding="sm" ta="center">
          <Text size="xl" fw={700} c="green">
            {highCount}
          </Text>
          <Text size="xs" c="dimmed">
            Matched
          </Text>
        </Card>
        <Card withBorder padding="sm" ta="center">
          <Text size="xl" fw={700} c="yellow">
            {mediumCount}
          </Text>
          <Text size="xs" c="dimmed">
            Need Review
          </Text>
        </Card>
        <Card withBorder padding="sm" ta="center">
          <Text size="xl" fw={700} c="gray">
            {notFoundCount}
          </Text>
          <Text size="xs" c="dimmed">
            Not Found
          </Text>
        </Card>
        <Card withBorder padding="sm" ta="center">
          <Text size="xl" fw={700}>
            {allMatches.length}
          </Text>
          <Text size="xs" c="dimmed">
            Total Lookups
          </Text>
        </Card>
      </SimpleGrid>

      {matches.map((plMatch) => (
        <MatchMatrix key={plMatch.productLineId} plMatch={plMatch} />
      ))}
    </Stack>
  );
}

function MatchMatrix({
  plMatch,
}: {
  plMatch: ProductLineRetailerMatches;
}) {
  const retailers = ["Amazon", "Best Buy", "Walmart", "Target", "Costco"];

  return (
    <Card withBorder padding="md" radius="md">
      <Text fw={600} mb="sm">
        {plMatch.productLineName}
      </Text>

      <Table.ScrollContainer minWidth={700}>
        <Table fz="xs" verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>Size</Table.Th>
              <Table.Th w={160}>Model</Table.Th>
              <Table.Th w={70} ta="right">
                MSRP
              </Table.Th>
              {retailers.map((r) => (
                <Table.Th key={r} ta="center">
                  <Group gap={4} justify="center">
                    <LogoImg
                      domain={r === "Best Buy" ? "bestbuy.com" : r.toLowerCase() + ".com"}
                      alt={r}
                      size={14}
                    />
                    <Text size="xs" fw={500}>
                      {r}
                    </Text>
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {plMatch.variants.map((v) => (
              <Table.Tr key={v.modelNumber}>
                <Table.Td>
                  <Badge variant="light" color="gray" size="xs">
                    {v.variantLabel}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" size="xs">
                    {v.modelNumber}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="xs" fw={500}>
                    {formatPriceCompact(v.msrp)}
                  </Text>
                </Table.Td>
                {v.matches.map((match) => (
                  <Table.Td key={match.retailerId} ta="center">
                    <Tooltip
                      label={
                        <Stack gap={2}>
                          <Text size="xs">{match.matchReason}</Text>
                          {match.priceFound && (
                            <Text size="xs">
                              Price: {formatPriceCompact(match.priceFound)}
                              {match.priceFound !== v.msrp && (
                                <Text
                                  span
                                  c={
                                    match.priceFound < v.msrp
                                      ? "green"
                                      : "red"
                                  }
                                >
                                  {" "}
                                  ({match.priceFound < v.msrp ? "" : "+"}
                                  {formatPriceCompact(
                                    match.priceFound - v.msrp,
                                  )}
                                  )
                                </Text>
                              )}
                            </Text>
                          )}
                          {match.retailerSku && (
                            <Text size="xs" c="dimmed">
                              {match.retailerSku}
                            </Text>
                          )}
                          {match.url && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {match.url.replace(
                                /^https?:\/\/(www\.)?/,
                                "",
                              )}
                            </Text>
                          )}
                        </Stack>
                      }
                      multiline
                      w={350}
                      position="top"
                    >
                      <Box style={{ cursor: "pointer" }}>
                        <Group gap={4} justify="center">
                          {confidenceIcon(match.confidence)}
                          {match.priceFound ? (
                            <Text
                              size="xs"
                              c={
                                match.confidence === "not_found"
                                  ? "dimmed"
                                  : undefined
                              }
                            >
                              {formatPriceCompact(match.priceFound)}
                            </Text>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Group>
                      </Box>
                    </Tooltip>
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* Legend */}
      <Group gap="md" mt="xs">
        {(["high", "medium", "not_found"] as MatchConfidence[]).map((c) => (
          <Group key={c} gap={4}>
            {confidenceIcon(c)}
            {confidenceBadge(c)}
          </Group>
        ))}
      </Group>
    </Card>
  );
}

// --- Step 4: Confirm ---

function StepConfirm({
  catalog,
  selections,
  matches,
}: {
  catalog: DiscoveredBrandCatalog;
  selections: Record<string, boolean>;
  matches: ProductLineRetailerMatches[];
}) {
  const selectedLines = catalog.productLines.filter(
    (pl) => selections[pl.id],
  );
  const totalVariants = selectedLines.reduce(
    (s, pl) => s + pl.variants.length,
    0,
  );
  const allRetailerMatches = matches.flatMap((pl) =>
    pl.variants.flatMap((v) => v.matches),
  );
  const highMatches = allRetailerMatches.filter(
    (m) => m.confidence === "high",
  ).length;
  const mediumMatches = allRetailerMatches.filter(
    (m) => m.confidence === "medium",
  ).length;

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Review what will be created and begin tracking.
      </Text>

      <SimpleGrid cols={3}>
        <Card withBorder padding="md" ta="center">
          <Text size="xl" fw={700}>
            {selectedLines.length}
          </Text>
          <Text size="sm" c="dimmed">
            Product Lines
          </Text>
        </Card>
        <Card withBorder padding="md" ta="center">
          <Text size="xl" fw={700}>
            {totalVariants}
          </Text>
          <Text size="sm" c="dimmed">
            Size Variants (SKUs)
          </Text>
        </Card>
        <Card withBorder padding="md" ta="center">
          <Text size="xl" fw={700} c="green">
            {highMatches}
          </Text>
          <Text size="sm" c="dimmed">
            Retailer URLs Matched
          </Text>
        </Card>
      </SimpleGrid>

      <Card withBorder padding="md">
        <Text fw={600} mb="sm">
          Product lines to create
        </Text>
        <Table fz="sm" verticalSpacing="xs">
          <Table.Tbody>
            {selectedLines.map((pl) => (
              <Table.Tr key={pl.id}>
                <Table.Td>
                  <Text fw={500} size="sm">
                    {pl.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={tierColor(pl.tier)} variant="light">
                    {pl.tier}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {pl.displayTechnology}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {pl.variants.map((v) => (
                      <Badge
                        key={v.variantLabel}
                        variant="light"
                        color="gray"
                        size="xs"
                      >
                        {v.variantLabel}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      {mediumMatches > 0 && (
        <Card withBorder padding="sm" style={{ borderColor: "var(--mantine-color-yellow-4)" }}>
          <Group gap="sm">
            <IconCircleFilled
              size={10}
              style={{ color: "var(--mantine-color-yellow-6)" }}
            />
            <Text size="sm">
              <Text span fw={600}>
                {mediumMatches} retailer matches need review.
              </Text>{" "}
              These will be imported but marked for verification. You can review
              them in the product detail page.
            </Text>
          </Group>
        </Card>
      )}

      <Button
        size="lg"
        leftSection={<IconRocket size={20} />}
        fullWidth
      >
        Import {selectedLines.length} Product Lines &amp; Start Tracking
      </Button>
    </Stack>
  );
}

// --- Main Wizard ---

export function DiscoverPage() {
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState<DiscoveredBrandCatalog | null>(null);
  const [selections, setSelections] = useState<Record<string, boolean>>({});
  const [matches, setMatches] = useState<ProductLineRetailerMatches[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);

  function handleCrawlComplete(cat: DiscoveredBrandCatalog) {
    setCatalog(cat);
    // Pre-select lines the catalog marks as selected
    const initial: Record<string, boolean> = {};
    for (const pl of cat.productLines) {
      initial[pl.id] = pl.selected;
    }
    setSelections(initial);
    // Stay on step 0 — admin reviews summary and clicks "Review & Select"
  }

  function handleToggle(id: string) {
    setSelections((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function handleStartMatching() {
    setStep(2);
    setSearching(true);
    setSearchProgress(0);

    // Simulate search progress
    const steps = [10, 20, 40, 60, 80, 95, 100];
    steps.forEach((p, i) => {
      setTimeout(() => {
        setSearchProgress(p);
        if (p === 100) {
          setTimeout(() => {
            // Filter matches to only selected product lines
            const selected = SAMSUNG_RETAILER_MATCHES.filter(
              (m) => selections[m.productLineId],
            );
            setMatches(selected);
            setSearching(false);
          }, 300);
        }
      }, (i + 1) * 500);
    });
  }

  const selectedCount = Object.values(selections).filter(Boolean).length;

  return (
    <>
      <Title order={2} mb="xs">
        Discover Products
      </Title>
      <Text size="sm" c="dimmed" mb="lg">
        Automatically discover product catalogs from manufacturer websites and
        find matching retailer listings.
      </Text>

      <Stepper
        active={step}
        onStepClick={(s) => {
          // Only allow going back
          if (s < step) setStep(s);
        }}
        mb="xl"
      >
        <Stepper.Step
          label="Crawl OEM"
          description="Scan manufacturer site"
          icon={<IconRadar size={18} />}
        />
        <Stepper.Step
          label="Review Catalog"
          description="Select product lines"
          icon={<IconCheck size={18} />}
        />
        <Stepper.Step
          label="Match Retailers"
          description="Find retailer URLs"
          icon={<IconSearch size={18} />}
        />
        <Stepper.Step
          label="Confirm"
          description="Import & start tracking"
          icon={<IconRocket size={18} />}
        />
      </Stepper>

      <Divider mb="lg" />

      {step === 0 && (
        <StepCrawl
          catalog={catalog}
          onCrawlComplete={handleCrawlComplete}
          onReview={() => setStep(1)}
        />
      )}

      {step === 1 && catalog && (
        <>
          <StepReviewCatalog
            catalog={catalog}
            selections={selections}
            onToggle={handleToggle}
          />
          <Group justify="flex-end" mt="lg">
            <Button
              disabled={selectedCount === 0}
              onClick={handleStartMatching}
              leftSection={<IconSearch size={16} />}
            >
              Search Retailers for {selectedCount} Product Lines
            </Button>
          </Group>
        </>
      )}

      {step === 2 && (
        <>
          <StepRetailerMatching
            matches={matches}
            searching={searching}
            searchProgress={searchProgress}
          />
          {!searching && matches.length > 0 && (
            <Group justify="flex-end" mt="lg">
              <Button variant="light" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)} leftSection={<IconCheck size={16} />}>
                Confirm Matches
              </Button>
            </Group>
          )}
        </>
      )}

      {step === 3 && catalog && (
        <StepConfirm
          catalog={catalog}
          selections={selections}
          matches={matches}
        />
      )}
    </>
  );
}
